import React from 'react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import Masonry from 'react-masonry-css';
import ReactMarkdown from 'react-markdown';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import { Heading, Text, Link } from '../components/typography';
import { Layout } from '../components/layout';
import './project-page.scss';

const ProjectPage = ({ data }) => {
    const post = data.markdownRemark;
    const breakpointColumnsObj = {
        default: 2,
        767: 1
    };

    const images = sortBy(data.allFile?.edges, (edge) => {
        return edge?.node?.childImageSharp?.parent?.name;
    });

    const {
        title,
        date,
        short_description,
        description,
        thumbnail,
        ongoing
    } = post?.frontmatter;

    const seoImage =
        thumbnail?.img?.childImageSharp?.gatsbyImageData?.images?.fallback?.src;


    const renderImage = (image, index) => {
        const imageData = image?.node?.childImageSharp?.gatsbyImageData


        return (
            <GatsbyImage
                image={
                    imageData
                }
                objectFit="contain"
                key={index}
                className="image"
                alt={`${title} - ${index}`}
            />);

    }

    return (
        <Layout
            className="project-page"
            seoData={{
                title,
                description: description || "Artwork by Andrew Duna",
                image: seoImage,
            }}
        >
            <article
                itemScope
                itemType="http://schema.org/CreativeWork"
                className="project-page--content"
            >
                <meta itemProp="headline name" content={title} />
                <meta itemProp="keywords" content={short_description} />
                <meta
                    itemProp="thumbnailUrl image"
                    content={`https://andrewduna.com${seoImage}`}
                />
                <meta itemProp="author publisher" content="Andrew Duna" />


                <Heading
                    className="project-page--title"
                    size="large"
                    level="1"
                    itemProp="headline name"
                >
                    {title}
                </Heading>
                <div className="project-page--description-container">
                    {description && (
                        <div
                            className="project-page--description text text_large"
                            itemProp="description"
                        >
                            <ReactMarkdown
                                components={{
                                    a: ({ children, ...props }) => (
                                        <Link large external newTab linkUrl={props.href}>{children}</Link>
                                    ),
                                }}>{description}</ReactMarkdown>
                        </div>
                    )}

                    <div className="project-page--metadata">
                        {short_description && (
                            <Text
                                className="project-page--short-description"
                                size="medium"
                                itemProp="description"
                            >
                                ({short_description})
                            </Text>
                        )}
                        {date && (
                            <Text
                                className="project-page--date"
                                size="medium"
                                itemProp="publishedDate"
                            >
                                {date}{ongoing && "–present"}
                            </Text>
                        )}
                    </div></div>

                <div className="project-page--image-list">
                    {images.length > 1 ?
                        <Masonry
                            breakpointCols={breakpointColumnsObj}
                            className="image-grid"
                            columnClassName="image-grid--column"
                        >
                            {map(images, (image, index) => (renderImage(image, index)))}</Masonry> : <div className="single-image">{renderImage(images[0], 1)}</div>
                    }
                    {post?.html && (
                        <section
                            className="markdown"
                            dangerouslySetInnerHTML={{ __html: post?.html }}
                        />
                    )}
                </div>
            </article>
        </Layout>
    );
};

export default ProjectPage;

export const pageQuery = graphql`
    query ArchiveBySlug($id: String!, $slug: String!) {
        site {
            siteMetadata {
                title
            }
        }
        markdownRemark(id: { eq: $id }) {
            id
            html
            frontmatter {
                title
                slug
                date(formatString: "YYYY")
                description
                short_description
                medium
                ongoing
                category
                thumbnail {
                    img {
                        childImageSharp {
                            gatsbyImageData
                        }
                    }
                }
            }
        }
        allFile(
            filter: {
                relativeDirectory: { eq: $slug }
                extension: { in: ["jpg", "jpeg", "png"] }
                        name: { regex: "/^(?!.*thumbnail).*$/i" }

            }
        ) {
            edges {
                node {
                    childImageSharp {
                        gatsbyImageData(layout: FULL_WIDTH)
                        parent {
                            ... on File {
                                name
                            }
                        }
                    }
                }
            }
        }
    }
`;
